import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Button, Card, CardBody, Col, Container, Form, InputGroup, Label, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWindowClose } from "@fortawesome/free-solid-svg-icons";

import { MySpinner } from "components/MySpinner";
import useQuery from "utils/useQuery";
import { downloadAnaliticsProvider, generateAnaliticsProvider, getAnaliticsProvider, getProgressAnaliticsProvider } from "services/apiAnalitics";
import { Pagination } from "components/Pagination";
import { AnaliticsProviderTable } from "features/AnaliticsProvider/AnaliticsProviderTable";
import { dateTimeFormat } from "utils/format";
import { SelectProvider } from "components/SelectProvider";

import "./analiticsProvider.scss";

const SORT_FIELDS = [
  {
    label: "Остаток долга на сегодня ↑",
    value: "remainingDebt_asc",
    by: "remainingDebt",
    dir: "asc",
  },
  {
    label: "Остаток долга на сегодня ↓",
    value: "remainingDebt_desc",
    by: "remainingDebt",
    dir: "desc",
  },
];

export const AnaliticsProviderPage = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const query = useQuery(useLocation);
  const [analiticsProvider, setAnaliticsProvider] = useState([]);
  const [analiticsProviderCount, setAnaliticsProviderCount] = useState(0);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [intervalProgress, setIntervalProgress] = useState(null);
  const [isShowReset, setIsShowReset] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    query.get("page") ? Number(query.get("page")) : 1
  );

  const getPageSize = localStorage.getItem("pageSize") ? JSON.parse(localStorage.getItem("pageSize")) : {};
  const [pageSize, setPageSize] = useState(getPageSize[pathname] || 10);

  const [provider, setProvider] = useState(
    query.get("provider")
      ? { label: query.get("providerName"), value: query.get("provider") }
      : null
  );

  const sortByDefault = query.get("sortBy")
    ? SORT_FIELDS.find(
      (val) => val.value === `${query.get("sortBy")}_${query.get("sortDir")}`
    )
    : null;
  const [sort, setSort] = useState(sortByDefault);

  const getAndSetAnaliticsProvider = () => {
    getAnaliticsProvider({
      paginationCount: pageSize,
      paginationOffset: (currentPage - 1) * pageSize,
      provider: provider?.value,
      sort
    })
      .then((res) => {
        setAnaliticsProvider(res.items);
        setAnaliticsProviderCount(res.totalItems);
        setLastUpdate(res.dateLastUpdate);
        // setRemainingDebt(res.totalRemainingDebt);
        // setRemainingDebtExpired(res.totalRemainingDebtExpired);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  };

  const generateAnaliticsProviderHandler = () => {
    setLoadingGenerate(true);
    toast.success("Генерация отчета запущена");
    generateAnaliticsProvider()
      .then((res) => { })
      .catch((e) => {
        toast.error(e.response?.data?.message);
      });
  };

  useEffect(() => {
    setLoading(true);
    getAndSetAnaliticsProvider();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sort]);

  useEffect(() => {
    const url = new URLSearchParams();
    if (currentPage !== 1) {
      url.append("page", currentPage);
    }

    if (provider?.value) {
      url.append("provider", provider?.value);
      url.append("providerName", provider?.label);
    }

    if (url) {
      history.push(`${pathname}?${url}`);
    } else {
      history.push(pathname);
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, provider, sort]);

  const getAndSetProgress = () => {
    getProgressAnaliticsProvider().then((res) => {
      setProgress(res);

      if (typeof res?.progress !== "undefined") {
        setLoadingGenerate(true);
      } else {
        if (loadingGenerate) {
          toast.success("Генерация отчета завершена");
          setLoadingGenerate(false);
        }
      }
    });
  };

  useEffect(() => {
    getAndSetProgress();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadingGenerate) {
      setIntervalProgress(
        setInterval(() => {
          getAndSetProgress();
        }, 1000)
      );
    } else {
      clearInterval(intervalProgress);
      setIntervalProgress(null);
      setLoadingGenerate(false);
    }

    return () => {
      clearInterval(intervalProgress);
    };
  }, [loadingGenerate]);

  const downloadHandler = () => {
    setLoading(true);
    downloadAnaliticsProvider({
      sort,
      provider: provider?.value,
    })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Аналитика задолженности поставщикам.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const resetFilter = () => {
    setProvider(null);
  };

  const isSearch = () => {
    if (provider?.value || sort) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    setIsShowReset(isSearch());
    if (!isSearch()) {
      setLoading(true);
      getAndSetAnaliticsProvider();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.value, sort]);


  const searchHandler = () => {
    if (!isSearch()) return;
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    setLoading(true);
    getAndSetAnaliticsProvider();
  };

  const onSort = (field, order) => {
    setSort({
      by: field,
      dir: order,
    });
  };

  return (
    <Container fluid className="p-0 analitics-provider">
      {loading && <MySpinner overlay />}
      <Card className="mb-0 filter">
        <CardBody>
          <h1 className="h3 mb-3">Аналитика задолженности поставщикам</h1>
          <Form className="mb-3">
            <Row>
              <Col md={3}>
                <Label>Поставщик</Label>
                <SelectProvider provider={provider} setProvider={setProvider} />
              </Col>
              <Col>
                <InputGroup className="with-label">
                  {isShowReset && (
                    <Button color="danger" onClick={() => resetFilter()}>
                      <FontAwesomeIcon icon={faWindowClose} />
                    </Button>
                  )}
                  <Button
                    color="primary"
                    onClick={searchHandler}
                    className="button-wide"
                  >
                    Поиск
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          </Form>

          <Row>
            <Col md="3" className="d-flex flex-column align-items-start">
              <Button
                color="primary"
                className="button-wide btn btn-primary"
                onClick={generateAnaliticsProviderHandler}
                disabled={loadingGenerate}
              >
                Сгенерировать
                {loadingGenerate && (
                  <div className="spinner-wrapper">
                    <Spinner color="danger" size="sm" />
                  </div>
                )}
              </Button>
              {loadingGenerate && (
                <>Процесс генерации: {progress?.progress} %</>
              )}
            </Col>
            <Col md="3">
              <Button
                color="primary"
                className="button-wide btn btn-primary"
                onClick={downloadHandler}
              >
                Скачать
              </Button>
            </Col>
            <Col md="3">Дата обновления: {dateTimeFormat(lastUpdate)}</Col>
          </Row>
        </CardBody>
      </Card>

      <div>
        <AnaliticsProviderTable
          analiticsProvider={analiticsProvider}
          onSort={onSort}
        />

        <Card>
          <CardBody>
            <Pagination
              totalCount={analiticsProviderCount}
              currentPage={currentPage}
              pageSize={pageSize}
              setCurrentPage={setCurrentPage}
              onChangePageSize={setPageSize}
            />
          </CardBody>
        </Card>
      </div>
    </Container>
  );
}